<template>
  <div class="input-wrapper" @click="inputRef?.focus()" :class="{ error: formField.hasError.value }">
    <slot name="prefix"></slot>
    <input v-bind="inputArgs" :value="modelValue" :id="formField.id" @input="onChange" ref="inputRef" />
    <slot name="suffix"></slot>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, useAttrs } from 'vue';

import { FORM_FIELD_TOKEN } from './form-field/form-field.token';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const props = defineProps(['modelValue']);
const inputArgs = computed(() => ({ ...props, ...attrs }));

const inputRef = ref<HTMLInputElement | null>(null);
const emit = defineEmits(['update:modelValue']);
const formField = inject(FORM_FIELD_TOKEN, {
  id: attrs.id as string,
  hasError: ref(false),
});

function onChange(event: Event): void {
  let value: number | string | null = (event.target as HTMLInputElement).value!;

  if ((event.target as HTMLInputElement).type === 'number') {
    value = value !== '' ? Number(value) : null;
  }

  emit('update:modelValue', value);
}

defineExpose({
  inputElement: inputRef,
});
</script>

<style scoped lang="scss">
.input-wrapper {
  display: inline-flex;
  gap: var(--t-spacing-sm);
  align-items: center;
  height: var(--t-spacing-xxl);

  border: 1px solid var(--t-color-neutral-400);
  border-radius: var(--t-border-radius-sm);
  padding-inline: var(--t-spacing-md);

  font: var(--t-typography-body-md-font);
  color: var(--t-color-neutral-700);
  background-color: var(--t-color-neutral-white);

  &:has(input:focus) {
    color: var(--t-color-neutral-900);
    border-color: var(--t-color-neutral-900);
  }

  &.error {
    border-color: var(--t-color-red-400);
  }
}

input {
  appearance: none;
  width: 100%;
  height: 100%;

  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  outline: none;
  border: none;
  background: transparent;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--t-color-neutral-500);
  }

  &::-webkit-calendar-picker-indicator {
    color: var(--t-color-neutral-900);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    border: none;
    -webkit-text-fill-color: inherit;
    -webkit-box-shadow: 0 0 0px 1000px var(--t-color-neutral-white) inset;
  }
}
</style>
